import { AutocompleteFilter } from '@shigo-ui/ui';
import { useAgencyCampaignContext } from '../../../agencyCampaignContext';
import { AgencyCampaignSection, AgencyEditorMode } from '../../../../agency/model';
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardTableFilterTrigger,
  DashboardTableSearchInput,
  dashboardTableSelectTriggerClassName,
} from '@shigo-ui/dashboard-ui';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { useCampaignBrandOptions } from '../../../useCampaignBrandOptions';
import { useCreativeMutations, useCreativesQuery } from '../../hooks';
import {
  creativePageSizes,
  creativeStatusOptions,
  CreativeTab,
  resolveCreativeStatusLabel,
  serializeCreativeVariantsUpdate,
  type Creative,
  type CreativeStatus,
} from '../../model';
import { useCreativesRouteFilters } from '../../useCreativesRouteFilters';
import { CreativePreviewDialog } from '../CreativePreviewDialog';
import { CreativeVariantsDialog } from '../CreativeVariantsDialog';
import { buildCreativesTableColumns } from './CreativesTable.columns';

export function CreativesTable({ tab }: { tab: CreativeTab }) {
  const scope = useAgencyCampaignContext();
  const { setAgencies, filters, queryFilter, setBrand, setPagination, setSearch, setStatuses } =
    useCreativesRouteFilters(tab);
  const query = useCreativesQuery(queryFilter);
  const brands = useCampaignBrandOptions();
  const mutations = useCreativeMutations();
  const [previewCreative, setPreviewCreative] = React.useState<Creative | null>(null);
  const [variantsCreative, setVariantsCreative] = React.useState<Creative | null>(null);
  const [duplicatingIds, setDuplicatingIds] = React.useState<ReadonlySet<string>>(() => new Set());
  const [searchState, setSearchState] = React.useState(() => ({
    source: filters.name,
    value: filters.name,
  }));
  const searchDraft = searchState.source === filters.name ? searchState.value : filters.name;
  const submitSearch = React.useEffectEvent(setSearch);
  React.useEffect(() => {
    if (searchDraft === filters.name) return;
    const timeout = window.setTimeout(() => {
      submitSearch(searchDraft);
    }, 650);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [filters.name, searchDraft]);
  React.useEffect(() => {
    const firstBrand = brands.data?.[0];
    if (!filters.brandId && firstBrand) {
      setBrand(firstBrand.id);
    }
  }, [brands.data, filters.brandId, setBrand]);
  const columns = React.useMemo(
    () =>
      buildCreativesTableColumns({
        tab,
        agencyId: scope?.agency.id,
        brandId: filters.brandId,
        duplicatingIds,
        onPreview: setPreviewCreative,
        onVariants: setVariantsCreative,
        onDuplicate: (creative) => {
          setDuplicatingIds((current) => new Set(current).add(creative.id));
          void mutations.duplicate
            .mutateAsync({ brandId: filters.brandId, creativeId: creative.id })
            .then(() => toast.success(dashboardT('creatives.duplicated')))
            .catch((error: unknown) => {
              showRequestError(error, dashboardT('creatives.duplicateError'));
            })
            .finally(() => {
              setDuplicatingIds((current) => {
                const next = new Set(current);
                next.delete(creative.id);
                return next;
              });
            });
        },
      }),
    [duplicatingIds, filters.brandId, mutations.duplicate, tab, scope?.agency.id],
  );
  const createTo =
    tab === CreativeTab.Display
      ? '/campaigns/creatives/display/create'
      : '/campaigns/creatives/video/create';
  return (
    <main className="page-gutter min-w-0 flex-1 py-4 md:py-6">
      <DashboardDataTable
        title={dashboardT(
          tab === CreativeTab.Display ? 'creatives.title.display' : 'creatives.title.video',
        )}
        tableLabel={dashboardT(
          tab === CreativeTab.Display ? 'creatives.title.display' : 'creatives.title.video',
        )}
        columns={columns}
        items={filters.brandId ? (query.data?.content ?? []) : []}
        getRowKey={(creative) => creative.id}
        loading={Boolean(
          brands.isLoading ||
          (!filters.brandId && brands.data?.length) ||
          (filters.brandId && query.isFetching),
        )}
        loadingMessage={dashboardT('creatives.loading')}
        emptyMessage={
          filters.brandId ? dashboardT('creatives.empty') : dashboardT('creatives.brandRequired')
        }
        errorMessage={query.isError ? dashboardT('creatives.loadError') : undefined}
        minWidthClassName={tab === CreativeTab.Display ? 'min-w-[1000px]' : 'min-w-[920px]'}
        toolbar={
          <>
            {!scope ? (
              <AutocompleteFilter
                label={dashboardT('agencyAssignment.filter')}
                options={[]}
                selectedIds={filters.agencyIds ?? []}
                onSelectedIdsChange={setAgencies}
                selectAll={false}
                emptyMessage={dashboardT('agencyWorkspace.unavailable')}
                className="hidden"
              />
            ) : null}
            <Select
              {...(filters.brandId ? { value: filters.brandId } : {})}
              onValueChange={setBrand}
            >
              <SelectTrigger
                className={dashboardTableSelectTriggerClassName('w-48')}
                aria-label={dashboardT('creatives.brand')}
              >
                <SelectValue placeholder={dashboardT('creatives.selectBrand')} />
              </SelectTrigger>
              <SelectContent>
                {(brands.data ?? []).map((brand) => (
                  <SelectItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DashboardTableSearchInput
              value={searchDraft}
              aria-label={dashboardT('creatives.search')}
              placeholder={dashboardT('creatives.search')}
              onChange={(event) => {
                setSearchState({ source: filters.name, value: event.target.value });
              }}
            />
            <CreativeStatusFilter value={filters.activeStatus} onChange={setStatuses} />
            {filters.brandId ? (
              <Button asChild>
                <Link
                  to={scope ? '/a/$agencyId/campaigns/$section' : createTo}
                  params={{
                    agencyId: scope?.agency.id ?? '',
                    section: AgencyCampaignSection.Creatives,
                  }}
                  search={{
                    brand: filters.brandId,
                    ...(scope ? { editor: AgencyEditorMode.Create, creativeTab: tab } : {}),
                  }}
                >
                  <Plus className="h-4 w-4" />
                  {dashboardT(
                    tab === CreativeTab.Display
                      ? 'creatives.create.display'
                      : 'creatives.create.video',
                  )}
                </Link>
              </Button>
            ) : (
              <Button disabled>
                <Plus className="h-4 w-4" />
                {dashboardT(
                  tab === CreativeTab.Display
                    ? 'creatives.create.display'
                    : 'creatives.create.video',
                )}
              </Button>
            )}
          </>
        }
        pagination={{
          value: {
            page: filters.page,
            pageSize: filters.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: creativePageSizes,
          disabled: query.isFetching || !filters.brandId,
          onChange: (next) => {
            setPagination(next.page, next.pageSize);
          },
        }}
      />
      <CreativePreviewDialog
        creative={previewCreative}
        onOpenChange={(open) => {
          if (!open) setPreviewCreative(null);
        }}
      />
      <CreativeVariantsDialog
        key={variantsCreative?.id ?? 'closed'}
        creative={variantsCreative}
        saving={mutations.update.isPending}
        onOpenChange={(open) => {
          if (!open) setVariantsCreative(null);
        }}
        onSave={(variants) => {
          if (!variantsCreative) return;
          void mutations.update
            .mutateAsync({
              brandId: filters.brandId,
              creativeId: variantsCreative.id,
              payload: serializeCreativeVariantsUpdate(variantsCreative, variants),
            })
            .then(() => {
              toast.success(dashboardT('creatives.updated'));
              setVariantsCreative(null);
            })
            .catch((error: unknown) => {
              showRequestError(error, dashboardT('creatives.saveError'));
            });
        }}
      />
    </main>
  );
}

function CreativeStatusFilter({
  value,
  onChange,
}: {
  value: CreativeStatus[];
  onChange: (value: CreativeStatus[]) => void;
}) {
  const selectedStatuses = new Set(value);
  const label = value.length
    ? value.map(resolveCreativeStatusLabel).join(', ')
    : dashboardT('creatives.allStatuses');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <DashboardTableFilterTrigger
          label={label}
          aria-label={dashboardT('creatives.statusFilter')}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {creativeStatusOptions.map((status) => (
          <DropdownMenuCheckboxItem
            key={status}
            checked={selectedStatuses.has(status)}
            onSelect={(event) => {
              event.preventDefault();
            }}
            onCheckedChange={(checked) => {
              onChange(
                checked
                  ? Array.from(new Set([...value, status]))
                  : value.filter((item) => item !== status),
              );
            }}
          >
            {resolveCreativeStatusLabel(status)}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
